import React, { useCallback, useState, useEffect, useMemo } from 'react'
import NormalLayoutContainer from '../shared/NormalLayoutContainer'
import styles from './index.module.scss'
import { Spinner } from '../shared/Spinner'
import { ArrayParam, StringParam, useQueryParams, ObjectParam } from 'use-query-params'
import ARFilterForm from '../../components/ar-dashboard/ar-filter-form/index.js'
import ARDashboardCharts from '../../components/ar-dashboard/ar-dashboard-chart/index.js'
import QUERY_KEYS from '../../graphql/queryKeys'
import usersQuery from '../../graphql/queries/users'
import notify from '../../utilities/notify'
import useQuery from '../../hooks/useQuery'
import { useDispatch, useSelector } from 'react-redux'
import clientsAll from '../../graphql/queries/clientsAll'
import { captureErrorAndNotify } from '../../utilities/errorHandlers'
import { RECEIVE_ALL_CLIENTS_SUCCESS } from '../hauler/clients/action/clientAction.js'
import arDashboardQuery from '../../graphql/queries/arDashboard.js'
import arDashboardMonthlyDataQuery from '../../graphql/queries/arDashboardMonthlyData.js'

const pageSize = 6
export default function ARDashboard () {
  const { hauler } = useSelector(({ user }) => ({ hauler: user.hauler }))
  const [fakeCount, setFakeCount] = useState(null)
  const dispatch = useDispatch()
  const { allClients, user, dataLoaders } = useSelector((
    {
      allClients,
      user: { user },
      dataLoaders
    }
  ) => ({
    allClients,
    user,
    dataLoaders
  }))
  const fetchAllClients = useCallback(() => {
    clientsAll({
      dataLoaders,
      queryKey: [
        QUERY_KEYS.clientsAll,
        user.haulerId,
        { column: 'name', direction: 'DESC' },
        null,
        null,
        null,
        null,
        null
      ]
    })
      .then((resp) => {
        dispatch({ type: RECEIVE_ALL_CLIENTS_SUCCESS, payload: { clients: resp.clientsAll } })
      })
      .catch((error) => {
        captureErrorAndNotify(error, 'Error fetching Accounts')
      })
  }, [user, dataLoaders, dispatch])

  function onPageRequest (direction, cursor) {
    setQueryParamsReplace({ ...queryParams, pager: { cursor, direction, pageSize } })

    if (!fakeCount) return
    if (direction === 'before') {
      setFakeCount(prevFakeCount => ({ start: prevFakeCount.start - pageSize, end: prevFakeCount.end - pageSize }))
    }
    if (direction === 'after') {
      setFakeCount(prevFakeCount => ({ start: prevFakeCount.start + pageSize, end: prevFakeCount.end + pageSize }))
    }
  }

  useEffect(function onInitialRender () {
    fetchAllClients()
  }, [fetchAllClients])

  const sortColumn = 'FULL_NAME'
  const [queryParams, setQueryParams] = useQueryParams({
    startDate: StringParam,
    endDate: StringParam,
    collectors: ArrayParam,
    accounts: ArrayParam,
    status: ArrayParam,
    pager: ObjectParam
  })
  const searchParams = useMemo(() => {
    return {
      accountIds: queryParams.accounts?.length > 0 ? queryParams.accounts : null,
      collectorIds: queryParams.collectors?.length > 0 ? queryParams.collectors : null,
      status: queryParams?.status?.length > 0 ? queryParams.status : null,
      startDate: queryParams.startDate || null,
      endDate: queryParams.endDate || null
    }
  }, [queryParams])
  const setQueryParamsReplace = useCallback((params) => {
    setQueryParams(params, 'replace')
  }, [setQueryParams])

  const { data: { users } } = useQuery(
    [QUERY_KEYS.users, hauler.id, true, sortColumn],
    usersQuery,
    {
      onError () {
        notify('error', 'Error Fetching Users List')
      },
      initialData: { users: { nodes: [] } }
    }
  )

  const { data: dashboardData, isFetching } = useQuery([
    QUERY_KEYS.arDashboard,
    Object.fromEntries(Object.entries(searchParams).filter(([_, v]) => v))
  ],
  arDashboardQuery,
  {
    initialData: { arDashboard: {} },
    onError: (error) => {
      captureErrorAndNotify(error, 'Failed to fetch accounts recievable data')
    }
  })

  const { data: arDashboardMonthlyData, isFetching: isFetchingMonthly } = useQuery([
    QUERY_KEYS.arDashboardMonthlyData,
    Object.fromEntries(Object.entries(searchParams).filter(([_, v]) => v)),
    queryParams.pager
  ],
  arDashboardMonthlyDataQuery,
  {
    initialData: { arDashboardMonthlyData: {} },
    onError: (error) => {
      captureErrorAndNotify(error, 'Failed to fetch accounts recievable monthly data')
    }
  })

  const [arFilters, setArFilters] = useState({})
  useEffect(() => {
    setArFilters(prevState => ({
      collectors: users.nodes.filter(u => u.role === 'hauler_admin'),
      accounts: prevState.accounts
    }))
  }, [users])

  useEffect(() => {
    setArFilters(prevState => ({
      collectors: prevState.collectors,
      accounts: allClients.clients
    }))
  }, [allClients])

  function handleClearForm () {
    setFakeCount(null)
    setQueryParamsReplace({
      years: undefined,
      months: undefined,
      collectors: undefined,
      accounts: undefined
    })
  }

  useEffect(function toggleFakePagination () {
    if (isFetchingMonthly || fakeCount !== null || !arDashboardMonthlyData.arDashboardMonthlyData) return
    setFakeCount({ start: 1, end: Math.min(pageSize, arDashboardMonthlyData.arDashboardMonthlyData.totalCount) })
  }, [arDashboardMonthlyData, isFetchingMonthly, fakeCount, setFakeCount])

  return (
    <NormalLayoutContainer>
      <h1 className={styles.title}>Accounts Receivables</h1>
      <div className={styles.container}>
        <ARFilterForm
          handleInputChange={(formValues) => {
            setFakeCount(null)
            setQueryParamsReplace(formValues)
          }}
          showOptions={['dateRange', 'collectors', 'accounts', 'status']}
          options={{
            collectors: arFilters?.collectors?.map(opt => ({
              label: opt.firstName + ' ' + opt.lastName,
              value: opt.id
            })) || [],
            accounts: arFilters?.accounts?.map(opt => ({
              label: opt.name,
              value: opt.id
            })) || []
          }}
          initialValues={queryParams}
          clearSearch={handleClearForm}
        />
        <div className={styles.reportCharts}>
          <Spinner isFetching={dashboardData === undefined || isFetching}>
            <ARDashboardCharts
              data={dashboardData?.arDashboard}
              isFetching={isFetchingMonthly}
              fakeCount={fakeCount}
              onPageRequest={onPageRequest}
              monthlyData={arDashboardMonthlyData?.arDashboardMonthlyData}
            />
          </Spinner>
        </div>
      </div>
    </NormalLayoutContainer>
  )
}
